import React from "react";
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Collapse,
  Divider,
  Group,
  Menu,
  NumberInput,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconLock,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import {
  EXPENSE_CATEGORY_LABELS,
  ORANGE,
  bonusThreshold,
  currency,
  titleize,
  toScaleLines,
} from "./repDealDisplay";
import { nextEditorItemId } from "../../services/payExtraction";

// The editable half of a pay structure, shared by every surface that lets a
// human tune one: the Send Offer flow's Pay Details step and the AI pay-draft
// review (single document and bulk import). repDealDisplay.js is the read-only
// twin — these are the same tables with inputs in them.

export const normalizeScaleLines = lines =>
  toScaleLines(lines)
    .slice()
    .sort((a, b) => a.revenue_amount - b.revenue_amount)
    .map((line, i) => (i === 0 ? { ...line, revenue_amount: 0 } : line));

// Inline-editable rows for an added upfront pay, mirroring the iOS
// `upfrontRows`: the amounts are edited in place, while the pay type itself
// changes by removing and re-adding.
export const upfrontFields = (up, update) => {
  const out = [];
  if (up.fixed_amount != null) {
    out.push({
      label: "Amount",
      unit: "money",
      value: up.fixed_amount,
      onChange: v => update({ ...up, fixed_amount: v }),
    });
  }
  if (up.fixed_percentage != null) {
    out.push({
      label: "Percentage",
      unit: "percent",
      value: up.fixed_percentage,
      onChange: v => update({ ...up, fixed_percentage: v }),
    });
  }
  (up.scale_lines || []).forEach((line, i) => {
    const isAmount = line.amount != null;
    if (!isAmount && line.percentage == null) return;
    const accounts = Number(line.accounts_amount) || 0;
    out.push({
      label: `${accounts}+ accounts`,
      unit: isAmount ? "money" : "percent",
      value: isAmount ? line.amount : line.percentage,
      onChange: v =>
        update({
          ...up,
          scale_lines: up.scale_lines.map((l, index) =>
            index === i ? { ...l, [isAmount ? "amount" : "percentage"]: v } : l
          ),
        }),
    });
  });
  return out;
};

export const isRecurringBonus = category =>
  category === "RECURRING" || category === "REOCCURRING";

// Inline-editable rows for an added bonus. The amounts and the earning
// threshold are edited in place; the category and metric change via the form.
export const bonusFields = (b, update) => {
  const out = [];
  if (b.bonus_type_category === "ITEM") {
    const value = b.item_monetary_value
      ? ` (${currency(b.item_monetary_value)})`
      : "";
    out.push({ label: "Item", text: `${b.item_name || "Item"}${value}` });
  } else if (isRecurringBonus(b.bonus_type_category)) {
    if (b.recurring_initial_amount != null) {
      out.push({
        label: "Initial",
        unit: "money",
        value: b.recurring_initial_amount,
        onChange: v => update({ ...b, recurring_initial_amount: v }),
      });
    }
    if (b.recurring_repeated_amount != null) {
      const schedule = (b.recurring_schedule_type || "").toLowerCase();
      out.push({
        label: schedule ? `Recurring (${schedule})` : "Recurring",
        unit: "money",
        value: b.recurring_repeated_amount,
        onChange: v => update({ ...b, recurring_repeated_amount: v }),
      });
    }
  } else if (b.bonus_amount != null) {
    out.push({
      label: "Amount",
      unit: "money",
      value: b.bonus_amount,
      onChange: v => update({ ...b, bonus_amount: v }),
    });
  }

  const qualifier = [b.bonus_type_earned_by_type, b.bonus_type_earned_by_item]
    .filter(Boolean)
    .map(v => String(v).toLowerCase().replace(/_/g, " "))
    .join(" ");
  const label = qualifier ? `Earned at (${qualifier})` : "Earned at";
  switch (b.bonus_type_earned_by_item) {
    case "REVENUE":
      out.push({
        label,
        unit: "money",
        value: b.bonus_revenue_amount,
        onChange: v => update({ ...b, bonus_revenue_amount: v }),
      });
      break;
    case "ACCOUNTS":
      out.push({
        label,
        value: b.bonus_accounts_amount,
        onChange: v => update({ ...b, bonus_accounts_amount: v }),
      });
      // Per-account bonuses can be limited to accounts with a long-enough
      // agreement term (e.g. a "24-Month Bonus"). Blank = all accounts.
      out.push({
        label: "Minimum agreement term (months)",
        value: b.min_agreement_months,
        onChange: v => update({ ...b, min_agreement_months: v }),
      });
      break;
    case "RECRUITS":
      out.push({
        label,
        value: b.bonus_recruits_amount,
        onChange: v => update({ ...b, bonus_recruits_amount: v }),
      });
      break;
    default:
      out.push({ label: "Earned when", text: bonusThreshold(b) });
  }
  return out;
};

// Inline-editable rows for an added expense.
export const expenseFields = (e, update) => {
  const out = [];
  if (e.backend_fixed_amount != null) {
    out.push({
      label: "Amount",
      unit: "money",
      value: e.backend_fixed_amount,
      onChange: v => update({ ...e, backend_fixed_amount: v }),
    });
  }
  if (e.backend_percentage != null) {
    out.push({
      label: "Percentage",
      unit: "percent",
      value: e.backend_percentage,
      onChange: v => update({ ...e, backend_percentage: v }),
    });
  }
  if (e.recurring_frontend_amount != null) {
    const schedule = (e.recurring_schedule_type || "").toLowerCase();
    out.push({
      label: schedule ? `Recurring (${schedule})` : "Recurring",
      unit: "money",
      value: e.recurring_frontend_amount,
      onChange: v => update({ ...e, recurring_frontend_amount: v }),
    });
  }
  if (out.length === 0) {
    out.push({
      label: "Type",
      text:
        EXPENSE_CATEGORY_LABELS[e.expense_type_category] ||
        titleize(e.expense_type_category),
    });
  }
  return out;
};

// An editable revenue → percentage scale table. The first ("Start") row's
// revenue is locked at 0; the header doubles as a preset picker when the company
// has saved scales of this type. `readOnly` shows the same table with plain
// values, a lock on every row and no preset picker or add footer — a
// non-customizable template's scale can be read but not tuned.
//
// `period` / `onPeriodChange` (both optional) expose the scale's active window
// inside the selling year — `{start_date, end_date}` (YYYY-MM-DD) or null for
// the full season. The control only renders when `onPeriodChange` is provided;
// MD and override scales take a window, commission scales never do.
export function ScaleTable({
  title,
  subtitle,
  col2Label,
  lines,
  onChange,
  presets,
  readOnly = false,
  period = null,
  onPeriodChange,
  onEdit,
  onRemove,
}) {
  // Keeps the date inputs visible after "Set dates" even while both are still
  // empty (an empty pair reads back as null, i.e. full season).
  const [editingPeriod, setEditingPeriod] = React.useState(false);

  const rows =
    lines.length > 0 ? lines : [{ revenue_amount: 0, percentage: 0 }];

  const updateLine = (index, field, value) =>
    onChange(rows.map((l, i) => (i === index ? { ...l, [field]: value } : l)));

  const addLine = () =>
    onChange([...rows, { revenue_amount: 0, percentage: 0 }]);

  const removeLine = index => onChange(rows.filter((_, i) => i !== index));

  const hasPresets = !readOnly && Array.isArray(presets) && presets.length > 0;

  const lockIcon = <IconLock size={14} color="var(--mantine-color-gray-5)" />;

  const hasPeriod = !!(period && (period.start_date || period.end_date));
  const showPeriodInputs = hasPeriod || editingPeriod;

  const setPeriodField = (field, value) => {
    const next = {
      start_date: period?.start_date ?? null,
      end_date: period?.end_date ?? null,
      [field]: value || null,
    };
    onPeriodChange(next.start_date || next.end_date ? next : null);
  };

  const clearPeriod = () => {
    setEditingPeriod(false);
    onPeriodChange(null);
  };

  const dateInputStyle = {
    fontSize: 12,
    fontFamily: "inherit",
    padding: "2px 6px",
    border: "1px solid var(--mantine-color-gray-4)",
    borderRadius: 4,
    color: "inherit",
    background: "transparent",
  };

  const periodControl = onPeriodChange ? (
    <Group justify="center" gap={6} pb="xs" px="xs" wrap="wrap">
      <Text size="xs" c="dimmed">
        Active period:
      </Text>
      {showPeriodInputs ? (
        <>
          <input
            type="date"
            aria-label="Active period start"
            value={period?.start_date || ""}
            onChange={e => setPeriodField("start_date", e.target.value)}
            style={dateInputStyle}
          />
          <Text size="xs" c="dimmed">
            –
          </Text>
          <input
            type="date"
            aria-label="Active period end"
            value={period?.end_date || ""}
            onChange={e => setPeriodField("end_date", e.target.value)}
            style={dateInputStyle}
          />
          <UnstyledButton onClick={clearPeriod}>
            <Text size="xs" c="orange" fw={600}>
              Full season
            </Text>
          </UnstyledButton>
        </>
      ) : (
        <>
          <Text size="xs" fw={600}>
            Full season
          </Text>
          <UnstyledButton onClick={() => setEditingPeriod(true)}>
            <Text size="xs" c="orange" fw={600}>
              Set dates
            </Text>
          </UnstyledButton>
        </>
      )}
    </Group>
  ) : null;

  return (
    <Card withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
      {hasPresets ? (
        <Menu withinPortal position="bottom" width="target">
          <Menu.Target>
            <UnstyledButton style={{ width: "100%" }}>
              <Stack gap={0} py="xs">
                <Group justify="center" gap={4}>
                  <Text fw={700} size="sm">
                    {title}
                  </Text>
                  <IconChevronDown size={14} />
                </Group>
                {subtitle && (
                  <Text ta="center" size="xs" c="dimmed">
                    {subtitle}
                  </Text>
                )}
              </Stack>
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {presets.map((preset, i) => (
              <Menu.Item
                key={preset.id ?? i}
                onClick={() => onChange(toScaleLines(preset.scale_lines))}
              >
                {preset.name || `Scale ${preset.id}`}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      ) : (
        <Group
          justify="space-between"
          align="center"
          wrap="nowrap"
          px="sm"
          py="xs"
        >
          <Stack gap={0} style={{ flex: 1 }}>
            <Text fw={700} size="sm">
              {title}
            </Text>
            {subtitle && (
              <Text size="xs" c="dimmed">
                {subtitle}
              </Text>
            )}
          </Stack>
          {(onEdit || onRemove) && (
            <Group gap={4} wrap="nowrap">
              {onEdit && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={onEdit}
                  aria-label="Edit"
                >
                  <IconPencil size={15} />
                </ActionIcon>
              )}
              {onRemove && (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  onClick={onRemove}
                  aria-label="Remove"
                >
                  <IconTrash size={15} />
                </ActionIcon>
              )}
            </Group>
          )}
        </Group>
      )}
      {periodControl}
      <Divider />
      <Group gap={0} style={{ background: ORANGE }}>
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          size="sm"
          py="xs"
          c="white"
        >
          Sales
        </Text>
        <Divider orientation="vertical" color="rgba(255,255,255,0.4)" />
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          size="sm"
          py="xs"
          c="white"
        >
          {col2Label}
        </Text>
        <Box w={40} />
      </Group>
      <Divider />
      {/* Base tier — revenue locked at 0, shown as the number rather than a
          "Start" label so every tier reads as a revenue threshold. */}
      <Group gap={0} style={{ background: "var(--mantine-color-gray-1)" }}>
        <Box style={{ flex: 1 }} px="xs" py={6}>
          <Text ta="center" fw={700} size="sm">
            {currency(rows[0]?.revenue_amount ?? 0)}
          </Text>
        </Box>
        <Divider orientation="vertical" />
        <Box style={{ flex: 1 }} py={readOnly ? 6 : 4} px="xs">
          {readOnly ? (
            <Text ta="center" size="sm" fw={600}>
              {rows[0]?.percentage ?? 0}
            </Text>
          ) : (
            <NumberInput
              value={rows[0]?.percentage ?? 0}
              onChange={v => updateLine(0, "percentage", Number(v) || 0)}
              variant="filled"
              size="xs"
              styles={{ input: { textAlign: "center" } }}
              hideControls
            />
          )}
        </Box>
        <Box
          w={40}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {lockIcon}
        </Box>
      </Group>
      {rows.slice(1).map((line, i) => (
        <React.Fragment key={i}>
          <Divider />
          <Group gap={0}>
            <Box style={{ flex: 1 }} py={readOnly ? 6 : 4} px="xs">
              {readOnly ? (
                <Text ta="center" size="sm" fw={700}>
                  {currency(line.revenue_amount)}
                </Text>
              ) : (
                <NumberInput
                  value={line.revenue_amount}
                  onChange={v =>
                    updateLine(i + 1, "revenue_amount", Number(v) || 0)
                  }
                  variant="filled"
                  size="xs"
                  styles={{ input: { textAlign: "center" } }}
                  hideControls
                  thousandSeparator=","
                />
              )}
            </Box>
            <Divider orientation="vertical" />
            <Box style={{ flex: 1 }} py={readOnly ? 6 : 4} px="xs">
              {readOnly ? (
                <Text ta="center" size="sm" fw={600}>
                  {line.percentage}
                </Text>
              ) : (
                <NumberInput
                  value={line.percentage}
                  onChange={v =>
                    updateLine(i + 1, "percentage", Number(v) || 0)
                  }
                  variant="filled"
                  size="xs"
                  styles={{ input: { textAlign: "center" } }}
                  hideControls
                />
              )}
            </Box>
            <Box
              w={40}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {readOnly ? (
                lockIcon
              ) : (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  onClick={() => removeLine(i + 1)}
                  aria-label="Remove row"
                >
                  <IconTrash size={14} />
                </ActionIcon>
              )}
            </Box>
          </Group>
        </React.Fragment>
      ))}
      {!readOnly && (
        <>
          <Divider />
          <UnstyledButton
            onClick={addLine}
            style={{ display: "block", width: "100%" }}
          >
            <Text ta="center" py="xs" c="orange" fw={600} size="sm">
              + Add row
            </Text>
          </UnstyledButton>
        </>
      )}
    </Card>
  );
}

// The container for the Upfront Pay / Bonuses / Expenses sections, matching the
// scale tables' chrome: a centered title bar, the added items, and a full-width
// "+ Add" footer that expands the add form inline — items are added in place,
// the same way scale rows are. `onAdd` absent hides the footer (a locked
// template, or the one allowed upfront pay is already set); a locked section
// with nothing in it is hidden entirely, as on iOS.
export function ItemTable({ title, addLabel, onAdd, form, empty, children }) {
  if (empty && !onAdd && !form) return null;
  return (
    <Card withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
      <Text ta="center" fw={700} size="sm" py="xs">
        {title}
      </Text>
      {children}
      {form ? (
        <>
          <Divider />
          <Box p="sm">{form}</Box>
        </>
      ) : onAdd ? (
        <>
          <Divider />
          <UnstyledButton
            onClick={onAdd}
            style={{ display: "block", width: "100%" }}
          >
            <Text ta="center" py="xs" c="orange" fw={600} size="sm">
              + {addLabel}
            </Text>
          </UnstyledButton>
        </>
      ) : null}
    </Card>
  );
}

// One added pay-detail item inside an ItemTable: a centered title + subtitle
// (with a trash button) above its rows. `fields` renders the amounts as inline
// editable inputs; `rows` renders plain label/value pairs for a locked section.
export function ItemRow({ title, subtitle, note, fields, rows, onRemove }) {
  return (
    <>
      <Divider />
      <Group gap={0} px="xs" py={6} wrap="nowrap">
        <Box w={28} />
        <Stack gap={0} style={{ flex: 1 }}>
          <Text ta="center" fw={700} size="sm">
            {title}
          </Text>
          {subtitle && (
            <Text ta="center" size="xs" c="dimmed">
              {subtitle}
            </Text>
          )}
          {/* Where this item was read from — the AI pay-draft review quotes the
              document line under the values so they can be checked against it. */}
          {note && (
            <Text ta="center" size="xs" c="dimmed" fs="italic" mt={4}>
              {note}
            </Text>
          )}
        </Stack>
        <Box w={28} style={{ display: "flex", justifyContent: "center" }}>
          {onRemove && (
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              onClick={onRemove}
              aria-label="Remove"
            >
              <IconTrash size={14} />
            </ActionIcon>
          )}
        </Box>
      </Group>
      {(rows || []).map((row, i) => (
        <React.Fragment key={`row-${i}`}>
          <Divider />
          <Group gap="sm" px="md" py={8} wrap="nowrap" justify="space-between">
            <Text size="sm" c="dimmed">
              {row[0]}
            </Text>
            <Text size="sm" fw={600} ta="right">
              {row[1]}
            </Text>
          </Group>
        </React.Fragment>
      ))}
      {(fields || []).map((field, i) => (
        <React.Fragment key={`field-${i}`}>
          <Divider />
          <Group gap="sm" px="md" py={6} wrap="nowrap" justify="space-between">
            <Text size="sm" c="dimmed">
              {field.label}
            </Text>
            {field.onChange ? (
              <NumberInput
                value={field.value ?? ""}
                onChange={v => field.onChange(v === "" ? null : Number(v))}
                variant="filled"
                size="xs"
                w={130}
                hideControls
                styles={{ input: { textAlign: "right" } }}
                {...(field.unit === "money"
                  ? { prefix: "$", thousandSeparator: "," }
                  : field.unit === "percent"
                    ? { suffix: "%" }
                    : {})}
              />
            ) : (
              <Text size="sm" fw={600} ta="right">
                {field.text}
              </Text>
            )}
          </Group>
        </React.Fragment>
      ))}
    </>
  );
}

// ── Quality Deductions ─────────────────────────────────────────────────────
// Deal-level "Quality Deductions" trim a rep's production bonus. Two kinds:
//   TERM_BENCHMARK    — the "18-Month benchmark": accounts whose term reaches
//                       `threshold_months` are "long-term"; the deal must keep a
//                       long-term share stepped by total serviced contract value
//                       (`benchmark_tiers`). Falling short deducts
//                       `per_account_amount` per shortfall account for the first
//                       `first_tier_cap_pct` percent of the shortfall.
//   PER_ACCOUNT_FLAT  — Saved / Activated: `per_account_amount` off per account,
//                       `manual_count` accounts entered by hand.
// The editor keeps a local `_id` per row; it is stripped before the payload is
// sent (payDraftPayload / the deal-create map), same as bonuses / expenses.

export const REDUCTION_TYPE_LABELS = {
  TERM_BENCHMARK: "Term Benchmark",
  PER_ACCOUNT_FLAT: "Per-Account Flat",
};

// A sensible starting 18-month benchmark table: the required long-term
// share drops as total serviced contract value climbs.
const DEFAULT_BENCHMARK_TIERS = [
  { contract_value_threshold: 0, benchmark_percentage: 60 },
  { contract_value_threshold: 200000, benchmark_percentage: 55 },
  { contract_value_threshold: 400000, benchmark_percentage: 50 },
];

// A blank reduction of the given type, prefilled with the common defaults so the
// manager only tunes what differs. `makeId` lets each editor supply its own
// local-id source; falls back to the AI editor's sequence.
export const newReduction = (type, makeId = nextEditorItemId) => {
  const base = { _id: makeId(), reduction_type: type };
  if (type === "TERM_BENCHMARK") {
    return {
      ...base,
      label: "",
      threshold_months: 18,
      per_account_amount: 75,
      first_tier_cap_pct: 10,
      benchmark_tiers: DEFAULT_BENCHMARK_TIERS.map(t => ({ ...t })),
    };
  }
  return {
    ...base,
    label: "Saved Accounts",
    per_account_amount: 20,
    manual_count: null,
  };
};

// A fetched deal's reduction → the editor's working shape (adds a local `_id`,
// coerces numbers). Used to seed the editor from an existing deal or template.
export const seedReduction = (r, makeId = nextEditorItemId) => {
  const num = v => (v == null || v === "" ? null : Number(v));
  return {
    _id: makeId(),
    reduction_type: r.reduction_type,
    label: r.label ?? null,
    threshold_months: num(r.threshold_months),
    per_account_amount: num(r.per_account_amount),
    first_tier_cap_pct: num(r.first_tier_cap_pct),
    manual_count: num(r.manual_count),
    benchmark_tiers: (r.benchmark_tiers || []).map(t => ({
      contract_value_threshold: Number(t.contract_value_threshold) || 0,
      benchmark_percentage: Number(t.benchmark_percentage) || 0,
    })),
  };
};

// The editable benchmark-tiers table for a TERM_BENCHMARK reduction.
function BenchmarkTiersTable({ tiers, onChange }) {
  const rows = tiers || [];
  const updateRow = (i, field, value) =>
    onChange(rows.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)));
  const addRow = () =>
    onChange([
      ...rows,
      { contract_value_threshold: 0, benchmark_percentage: 50 },
    ]);
  const removeRow = i => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <Card withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
      <Group gap={0} style={{ background: ORANGE }}>
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          size="xs"
          py={6}
          c="white"
        >
          Contract value ≥
        </Text>
        <Divider orientation="vertical" color="rgba(255,255,255,0.4)" />
        <Text
          style={{ flex: 1 }}
          ta="center"
          fw={700}
          size="xs"
          py={6}
          c="white"
        >
          Required long-term %
        </Text>
        <Box w={40} />
      </Group>
      {rows.map((tier, i) => (
        <React.Fragment key={i}>
          <Divider />
          <Group gap={0} wrap="nowrap">
            <Box style={{ flex: 1 }} py={4} px="xs">
              <NumberInput
                value={tier.contract_value_threshold ?? 0}
                onChange={v =>
                  updateRow(i, "contract_value_threshold", Number(v) || 0)
                }
                variant="filled"
                size="xs"
                prefix="$"
                thousandSeparator=","
                hideControls
                styles={{ input: { textAlign: "center" } }}
              />
            </Box>
            <Divider orientation="vertical" />
            <Box style={{ flex: 1 }} py={4} px="xs">
              <NumberInput
                value={tier.benchmark_percentage ?? 0}
                onChange={v =>
                  updateRow(i, "benchmark_percentage", Number(v) || 0)
                }
                variant="filled"
                size="xs"
                suffix="%"
                hideControls
                styles={{ input: { textAlign: "center" } }}
              />
            </Box>
            <Box
              w={40}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                onClick={() => removeRow(i)}
                aria-label="Remove tier"
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Box>
          </Group>
        </React.Fragment>
      ))}
      <Divider />
      <UnstyledButton
        onClick={addRow}
        style={{ display: "block", width: "100%" }}
      >
        <Text ta="center" py="xs" c="orange" fw={600} size="sm">
          + Add tier
        </Text>
      </UnstyledButton>
    </Card>
  );
}

// One reduction's inputs, by type.
function ReductionRow({ reduction, onChange, onRemove }) {
  const r = reduction;
  const patch = next => onChange({ ...r, ...next });
  const numVal = v => (v === "" ? null : Number(v));

  return (
    <Card withBorder radius="md" p="sm">
      <Group justify="space-between" wrap="nowrap" mb="xs">
        <Text size="sm" fw={700}>
          {REDUCTION_TYPE_LABELS[r.reduction_type] || "Reduction"}
        </Text>
        <ActionIcon
          variant="subtle"
          color="red"
          size="sm"
          onClick={onRemove}
          aria-label="Remove reduction"
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Group>

      {r.reduction_type === "TERM_BENCHMARK" ? (
        <Stack gap="sm">
          <TextInput
            label="Label (optional)"
            placeholder="e.g. 18-Month Benchmark"
            value={r.label ?? ""}
            onChange={e => patch({ label: e.currentTarget.value })}
            size="xs"
          />
          <Group grow>
            <NumberInput
              label="Long-term term (months)"
              value={r.threshold_months ?? ""}
              onChange={v => patch({ threshold_months: numVal(v) })}
              size="xs"
              hideControls
            />
            <NumberInput
              label="$ per shortfall account"
              value={r.per_account_amount ?? ""}
              onChange={v => patch({ per_account_amount: numVal(v) })}
              size="xs"
              prefix="$"
              thousandSeparator=","
              hideControls
            />
            <NumberInput
              label="First-tier cap (%)"
              value={r.first_tier_cap_pct ?? ""}
              onChange={v => patch({ first_tier_cap_pct: numVal(v) })}
              size="xs"
              suffix="%"
              hideControls
            />
          </Group>
          <Text size="xs" c="dimmed">
            Accounts whose term reaches the long-term months count toward the
            benchmark. The required long-term share steps down as total serviced
            contract value climbs. Each shortfall account within the first-tier
            cap deducts the per-account amount; beyond the cap an excess formula
            reduces the contract-value base instead.
          </Text>
          <BenchmarkTiersTable
            tiers={r.benchmark_tiers}
            onChange={tiers => patch({ benchmark_tiers: tiers })}
          />
        </Stack>
      ) : (
        <Stack gap="sm">
          <TextInput
            label="Label"
            placeholder="e.g. Saved Accounts"
            value={r.label ?? ""}
            onChange={e => patch({ label: e.currentTarget.value })}
            size="xs"
          />
          <Group grow>
            <NumberInput
              label="$ per account"
              value={r.per_account_amount ?? ""}
              onChange={v => patch({ per_account_amount: numVal(v) })}
              size="xs"
              prefix="$"
              thousandSeparator=","
              hideControls
            />
            <NumberInput
              label="Manual count"
              value={r.manual_count ?? ""}
              onChange={v => patch({ manual_count: numVal(v) })}
              size="xs"
              hideControls
            />
          </Group>
          <Text size="xs" c="dimmed">
            A flat deduction per account. Enter the account count by hand until
            per-account data is available.
          </Text>
        </Stack>
      )}
    </Card>
  );
}

// The collapsible "Quality Deductions" section for a deal pay editor. `reductions`
// is the editor state (rows carry a local `_id`); `onChange` replaces the list.
// `makeId` supplies local ids for added rows.
export function ReductionFactorsSection({
  reductions,
  onChange,
  makeId = nextEditorItemId,
}) {
  const list = reductions || [];
  const [open, setOpen] = React.useState(list.length > 0);

  const add = type => onChange([...list, newReduction(type, makeId)]);
  const update = updated =>
    onChange(list.map(r => (r._id === updated._id ? updated : r)));
  const remove = id => onChange(list.filter(r => r._id !== id));

  return (
    <Card withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
      <UnstyledButton
        onClick={() => setOpen(o => !o)}
        style={{ display: "block", width: "100%" }}
      >
        <Group justify="space-between" px="md" py="sm" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            {open ? (
              <IconChevronDown size={16} />
            ) : (
              <IconChevronRight size={16} />
            )}
            <Text fw={700} size="sm">
              Quality Deductions
            </Text>
          </Group>
          {list.length > 0 && (
            <Text size="xs" c="dimmed">
              {list.length} factor{list.length === 1 ? "" : "s"}
            </Text>
          )}
        </Group>
      </UnstyledButton>
      <Collapse in={open}>
        <Divider />
        <Stack gap="sm" p="sm">
          <Text size="xs" c="dimmed">
            Quality Deductions trim the rep&apos;s production bonus — a term
            benchmark on long-term accounts, or a flat per-account deduction.
          </Text>
          {list.map(reduction => (
            <ReductionRow
              key={reduction._id}
              reduction={reduction}
              onChange={update}
              onRemove={() => remove(reduction._id)}
            />
          ))}
          <Menu withinPortal position="bottom-start">
            <Menu.Target>
              <Button
                variant="light"
                color="orange"
                size="xs"
                leftSection={<IconPlus size={14} />}
              >
                Add quality deduction
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => add("TERM_BENCHMARK")}>
                Term Benchmark
              </Menu.Item>
              <Menu.Item onClick={() => add("PER_ACCOUNT_FLAT")}>
                Per-Account Flat
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Stack>
      </Collapse>
    </Card>
  );
}
